import { useEffect, useMemo, useState } from "react";
import { fetchFxRates } from "../utils/fx";
import { createPortal } from "react-dom";
import {
  format, subDays, startOfDay, endOfDay, startOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Eye, Save, Download, Pencil as EditIcon, X, Link2 } from "lucide-react";
import { DateRange } from "react-date-range";
import toast, { Toaster } from "react-hot-toast";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../styles/calendar.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import moment from "moment-timezone";

// ─── Classes padrão ───────────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

const toSaoPaulo = (date) => moment.tz(date, "America/Sao_Paulo").toDate();

function pickCurrency(row) {
  const c = row.currency_code ?? row.currency ?? row.currencyCode ?? row.CURRENCY ?? "BRL";
  return String(c).trim().toUpperCase();
}

function pickAmount(row) {
  const v = row.amount ?? row.commission ?? row.value ?? row.total ?? row.total_amount ?? 0;
  return Number(v) || 0;
}

function mapSaleRow(r) {
  return { ...r, amount: pickAmount(r), currency: pickCurrency(r) };
}

// ─── Presets ──────────────────────────────────────────────────────────────────

const getPresets = () => {
  const now = toSaoPaulo(new Date());
  return [
    { label: "Hoje",         range: [{ startDate: startOfDay(now),           endDate: endOfDay(now),              key: "selection" }] },
    { label: "Ontem",        range: [{ startDate: startOfDay(subDays(now,1)), endDate: endOfDay(subDays(now,1)),   key: "selection" }] },
    { label: "Últimos 7 dias", range: [{ startDate: startOfDay(subDays(now,6)), endDate: endOfDay(now),            key: "selection" }] },
    { label: "Este mês",     range: [{ startDate: startOfMonth(now),          endDate: endOfDay(now),              key: "selection" }] },
  ];
};

// ─── PillRange ────────────────────────────────────────────────────────────────

function PillRange({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3 rounded-lg text-sm font-medium border transition ${
        active
          ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SalesNotifications() {
  const [sales, setSales] = useState([]);
  const [fx, setFx] = useState(null);
  const [fxLoading, setFxLoading] = useState(true);
  const [fxDate, setFxDate] = useState("");
  const [loading, setLoading] = useState(true);

  const [dateRange, setDateRange] = useState([
    { startDate: startOfDay(subDays(new Date(), 6)), endDate: endOfDay(new Date()), key: "selection" },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [activePreset, setActivePreset] = useState("Últimos 7 dias");
  const [quickRange, setQuickRange] = useState("today");
  const [allTime, setAllTime] = useState(false);

  const [saleToDelete, setSaleToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState({ plan: "free" });

  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const [tzSearch, setTzSearch] = useState("");
  const allTimezones = moment.tz.names();
  const filteredTimezones = allTimezones.filter((tz) =>
    tz.toLowerCase().includes(tzSearch.toLowerCase())
  );

  // ── FX ──────────────────────────────────────────────────────────────────────

  useEffect(() => {
    let active = true;
    const loadFx = async () => {
      setFxLoading(true);
      try {
        const data = await fetchFxRates(fxDate || undefined).catch((err) => { console.error("fetchFxRates falhou:", err); return null; });
        if (!active) return;
        setFx(data);
      } catch (err) {
        console.error("Erro geral ao carregar cotação:", err);
        if (!active) return;
        setFx(null);
      } finally {
        if (active) setFxLoading(false);
      }
    };
    loadFx();
    return () => { active = false; };
  }, [fxDate]);

  // ── Dark mode detect ─────────────────────────────────────────────────────────

  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // ── Presets handler ──────────────────────────────────────────────────────────

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setQuickRange(null);
    setAllTime(false);
    if (preset.range) { setDateRange(preset.range); setShowCalendar(false); }
    else { setShowCalendar(true); }
  };

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchSales = async () => {
    setLoading(true);
    try {
      let url = "https://api.sobevenda.com.br/api-proxy/affiliate/get-sales";
      const start = allTime ? "2025-01-01" : format(dateRange[0].startDate, "yyyy-MM-dd");
      const end   = allTime ? format(new Date(), "yyyy-MM-dd") : format(dateRange[0].endDate, "yyyy-MM-dd");
      url += `?start_date=${start}&end_date=${end}`;
      const res = await fetch(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
      });
      const data = await res.json();
      const baseList = Array.isArray(data.sales) ? data.sales : [];
      const normalized = [...baseList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(mapSaleRow);
      setSales(normalized);
    } catch { setSales([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSales(); }, [dateRange[0].startDate, dateRange[0].endDate, allTime]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.sobevenda.com.br/api-proxy/affiliate/auth/me", {
          headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
        });
        setUser(await res.json());
      } catch { setUser({ plan: "free" }); }
    })();
  }, []);

  // ── Chart ────────────────────────────────────────────────────────────────────

  const chartData = useMemo(() => {
    let start, end;
    if (allTime) {
      if (!sales?.length) return [];
      const ts = sales.map((v) => new Date(v.created_at).getTime());
      start = startOfDay(new Date(Math.min(...ts)));
      end   = endOfDay(new Date(Math.max(...ts)));
    } else {
      start = startOfDay(dateRange[0].startDate);
      end   = endOfDay(dateRange[0].endDate);
    }
    const days = [];
    let curr = new Date(start);
    while (curr <= end) { days.push(new Date(curr)); curr.setDate(curr.getDate() + 1); }
    return days.map((d) => {
      const dateStr = format(d, "yyyy-MM-dd");
      const vendasDoDia = (sales || []).filter((v) => format(toSaoPaulo(v.created_at), "yyyy-MM-dd") === dateStr);
      return { dia: format(d, "dd/MM"), vendas: vendasDoDia.length, receita: 0 };
    });
  }, [sales, dateRange, allTime]);

  const chartColors = useMemo(() => ({
    grid: isDark ? "rgba(148,163,184,0.2)" : "rgba(100,116,139,0.2)",
    tick: isDark ? "#94A3B8" : "#64748B",
    bar: isDark ? "#CBD5E1" : "#0F172A",
    tooltipBg: "rgba(15,23,42,0.95)",
  }), [isDark]);

  // ── Sums ─────────────────────────────────────────────────────────────────────

  const sums = useMemo(() => sales.reduce((acc, s) => {
    const code = (s.currency_code || "BRL").toUpperCase();
    const amt = Number(s.amount) || 0;
    if (code === "USD") acc.USD += amt;
    else if (code === "EUR") acc.EUR += amt;
    else acc.BRL += amt;
    return acc;
  }, { USD: 0, EUR: 0, BRL: 0 }), [sales]);

  const totalBRL = useMemo(() => {
    const usdRate = fx?.USD_BRL ?? 0;
    const eurRate = fx?.EUR_BRL ?? 0;
    return sums.BRL + sums.USD * usdRate + sums.EUR * eurRate;
  }, [sums, fx]);

  // ── Export ───────────────────────────────────────────────────────────────────

  const exportCsv = () => {
    if (!sales.length) return;
    const valid = sales.filter((s) => s.gclid && s.conversion_name);
    if (!valid.length) { toast("Nenhuma venda válida para exportar (precisa de GCLID e Nome da Conversão)."); return; }
    const headers = ["Google Click ID", "Conversion Name", "Conversion Time", "Conversion Value", "Conversion Currency"];
    const lines = [headers.join(",")];
    valid.forEach((s) => {
      const rawDate = moment(s.created_at).tz(timezone).format("YYYY-MM-DD HH:mm:ssZ");
      const dateNoColon = rawDate.replace(/([+-]\d{2}):?(\d{2})$/, "$1$2");
      lines.push([s.gclid, s.conversion_name, dateNoColon, String(s.amount).replace(",", "."), s.currency_code || s.currency || "USD"].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `google-ads-conversions_${format(dateRange[0].startDate, "yyyyMMdd")}_a_${format(dateRange[0].endDate, "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Edit / Link / Delete ─────────────────────────────────────────────────────

  const handleShowEdit = (sale) => {
    setEditModal(sale);
    setEditForm({ visit_uuid: sale.visit_uuid || "", gclid: sale.gclid || "", gbraid: sale.gbraid || "", wbraid: sale.wbraid || "", amount: sale.amount || "", conversion_name: sale.conversion_name || "", visit_code: sale.visit_uuid || "" });
    setIsEditing(false);
  };
  const handleCloseEdit = () => setEditModal(null);
  const handleEditChange = (e) => { const { name, value } = e.target; setEditForm((p) => ({ ...p, [name]: value })); };

  const handleSaveEdit = async () => {
    try {
      await fetch(`https://api.sobevenda.com.br/api-proxy/affiliate/update-sale/${editModal.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
        body: JSON.stringify({ amount: editForm.amount ? parseFloat(String(editForm.amount).replace(",", ".")) : undefined, gclid: editForm.gclid, gbraid: editForm.gbraid, wbraid: editForm.wbraid, conversion_name: editForm.conversion_name, visit_uuid: (editForm.visit_code || editForm.visit_uuid || "").trim() }),
      });
      setEditModal(null);
      fetchSales();
    } catch { toast.error("Erro ao salvar edição."); }
  };

  const handleLinkByVisitCode = async () => {
    if (!editForm.visit_code) { toast.error("Digite o código da visita (svc_) para vincular."); return; }
    try {
      const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/lookup-visit?svc_code=${editForm.visit_code}`, { headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` } });
      const data = await res.json();
      if (data?.visit_uuid) {
        toast.success("Vinculado com sucesso!");
        await fetch(`https://api.sobevenda.com.br/api-proxy/script/visit/by-svc/${editForm.visit_code}/mark-conversion`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
          body: JSON.stringify({ orderid: editModal.orderid }),
        });
      } else { toast.error("Código não encontrado. Verifique e tente novamente."); }
    } catch { toast.error("Erro ao buscar o código."); }
  };

  const deleteSale = async (id) => {
    try {
      await fetch(`https://api.sobevenda.com.br/api-proxy/affiliate/archive-sale/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` } });
      setSales((prev) => prev.filter((s) => s.id !== id));
    } catch { toast.error("Erro ao deletar venda."); }
  };
  const handleDelete = (id) => { setSaleToDelete(id); setConfirmText(""); };
  const confirmDelete = () => { if (saleToDelete) { deleteSale(saleToDelete); setSaleToDelete(null); setConfirmText(""); } };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <Toaster />

      {/* Aviso */}
      <div className="mb-5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
        <div className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Atualização sobre o envio de conversões</div>
        <ul className="list-disc pl-5 space-y-1 text-xs">
          <li>Envio automático para o Google Ads <strong>a cada 10 minutos</strong>.</li>
          <li>O Google pode levar <strong>até 24 horas</strong> para exibir as conversões nos relatórios.</li>
          <li>Exporte e suba manualmente no Google Ads caso ainda não tenha conectado sua conta.</li>
          <li>Caso sua conversão veio sem o Gclid e está como PENDENTE, encontre a visita e vincule a venda.</li>
        </ul>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Notificações de Vendas</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Presets */}
          {getPresets().map((preset) => (
            <PillRange key={preset.label} active={activePreset === preset.label} onClick={() => handlePreset(preset)}>
              {preset.label}
            </PillRange>
          ))}
          <PillRange active={allTime} onClick={() => { setAllTime(true); setActivePreset("Todo período"); setQuickRange(null); setShowCalendar(false); }}>
            Todo período
          </PillRange>

          {/* Customizado */}
          <div className="relative">
            <button
              className={`h-9 px-3 rounded-lg border text-sm font-medium transition flex items-center gap-1.5 ${
                quickRange === "custom"
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
              onClick={() => { setShowCalendar((v) => !v); setQuickRange("custom"); setActivePreset(""); setAllTime(false); }}
            >
              <Calendar className="w-4 h-4" /> Customizado
            </button>

            {showCalendar && quickRange === "custom" && (
              <div className="absolute left-0 top-[44px] z-[9999] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 shadow-xl">
                <div className="sv-calendar">
                  <DateRange editableDateInputs onChange={(item) => setDateRange([item.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} maxDate={new Date()} locale={ptBR} />
                </div>
                <div className="flex justify-end gap-2 px-1 pb-1">
                  <button className="h-8 px-3 rounded-lg text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setShowCalendar(false)}>
                    Cancelar
                  </button>
                  <button
                    className="h-8 px-3 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                    onClick={() => { setShowCalendar(false); setQuickRange("custom"); setActivePreset(""); setAllTime(false); }}
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Timezone */}
          <div className="flex flex-col gap-1">
            <input className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-xs w-44 outline-none" placeholder="Buscar fuso horário..." value={tzSearch} onChange={(e) => setTzSearch(e.target.value)} />
            <select className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-xs w-44 outline-none" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {filteredTimezones.map((tz) => <option value={tz} key={tz}>{tz}</option>)}
            </select>
            <span className="text-[10px] text-slate-500">Só para exportação</span>
          </div>

          {/* Exportar */}
          <button onClick={exportCsv} className="h-9 inline-flex items-center gap-2 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Cards de stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Consolidado */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Consolidado em BRL</p>
          <p className="text-3xl font-extrabold mt-1 text-slate-900 dark:text-slate-100">{formatBRL(totalBRL)}</p>
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Total de vendas</p>
            <p className="text-2xl font-bold mt-0.5 text-slate-900 dark:text-slate-100">{sales.length}</p>
          </div>
        </div>

        {/* Receita por moeda */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">Receita por moeda</p>
          <div className="space-y-2 text-sm">
            {[{ code: "USD", sym: "$", val: sums.USD }, { code: "EUR", sym: "€", val: sums.EUR }, { code: "BRL", sym: "R$", val: sums.BRL }].map(({ code, sym, val }) => (
              <div key={code} className="flex items-center justify-between">
                <span className="text-slate-500 dark:text-slate-400">{code}</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{sym} {val.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cotação */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Cotação usada</p>
            <input type="date" className="text-xs rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1 bg-transparent text-slate-700 dark:text-slate-300 outline-none" value={fxDate} onChange={(e) => setFxDate(e.target.value)} title="Cotação do dia (vazio = hoje)" />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Conversão do dia</p>
          <p className="text-sm text-slate-900 dark:text-slate-100">
            1 USD ≈ <strong>R$ {(fx?.USD_BRL ?? 0).toFixed(4)}</strong>
            <span className="mx-2 text-slate-300 dark:text-slate-600">|</span>
            1 EUR ≈ <strong>R$ {(fx?.EUR_BRL ?? 0).toFixed(4)}</strong>
          </p>
          {!fxLoading && !fx && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Não foi possível obter a cotação — usando fallback.</p>
          )}
        </div>
      </div>

      {/* Gráfico */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 mb-6">
        <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4">Vendas por Dia</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.tick }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: chartColors.tick }} />
            <Tooltip contentStyle={{ background: chartColors.tooltipBg, color: "#fff", borderRadius: 8, border: "none", fontSize: 12 }} labelStyle={{ color: "#fff" }} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="vendas" fill={chartColors.bar} barSize={16} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabela */}
      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Carregando...</p>
      ) : sales.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Nenhuma venda encontrada nesse período.</p>
      ) : (
        <div className="overflow-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/40">
              <tr className="text-left">
                {["Data", "Plataforma", "Comissão", "Tipo", "Enviada ao Google", "Ações"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 ${i === 2 ? "text-right" : i >= 3 ? "text-center" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sales.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{format(toSaoPaulo(s.created_at), "dd/MM/yyyy HH:mm")}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{s.platform || "—"}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-slate-100">
                    {Number(s.amount).toFixed(2)} {(s.currency_code || s.currency || "").toUpperCase()}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">{s.type || s.conv_type || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {s.sent_to_google ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Enviada</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">Pendente</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-3">
                      <button className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100" onClick={() => handleShowEdit(s)}>
                        <Eye className="w-3.5 h-3.5" /> Ver mais
                      </button>
                      <button className="text-xs text-red-500 hover:text-red-600" onClick={() => handleDelete(s.id)}>
                        Deletar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODAL EDIÇÃO ── */}
      {editModal && createPortal(
        <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[1px] flex items-start" onClick={handleCloseEdit}
          style={{ left: "var(--sidebar-width,255px)", width: "calc(100vw - var(--sidebar-width,240px))" }}>
          <div className="rounded-2xl p-6 mt-8 ml-6 w-full max-w-2xl relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-3 right-3 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" onClick={handleCloseEdit}>
              <X className="w-4 h-4" />
            </button>

            <h3 className="text-base font-bold mb-4">Detalhes da Venda</h3>

            {/* Info cards */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "OrderID", value: editModal.orderid || "—", mono: true },
                { label: "Comissão", value: `${(editModal.currency_code || editModal.currency || "").toUpperCase()} ${Number(editModal.amount || 0).toFixed(2)}` },
                { label: "Tipo", value: editModal.type || editModal.conv_type || "—" },
              ].map(({ label, value, mono }) => (
                <div key={label} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">{label}</div>
                  <div className={`text-sm font-semibold break-all ${mono ? "font-mono" : ""}`}>{value}</div>
                </div>
              ))}
            </div>

            {/* Detalhes */}
            <div className="mb-4 grid md:grid-cols-2 gap-4">
              <div className="space-y-1 text-sm">
                <div><span className="text-slate-500">Data:</span> {format(toSaoPaulo(editModal.created_at), "dd/MM/yyyy HH:mm")}</div>
                <div><span className="text-slate-500">Plataforma:</span> {editModal.platform}</div>
                <div><span className="text-slate-500">Enviada ao Google:</span>{" "}{editModal.sent_to_google ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Sim</span> : <span className="text-slate-400">Não</span>}</div>
                <div><span className="text-slate-500">Moeda:</span> {(editModal.currency_code || editModal.currency || "—").toUpperCase()}</div>
                <div><span className="text-slate-500">Projeto:</span> {editModal.project_name || "—"}</div>
              </div>

              <div className="md:col-span-2 space-y-3">
                {/* Visit code */}
                <div>
                  <label className={labelCls}>Código da visita (svc_)</label>
                  <div className="flex gap-2">
                    <input name="visit_code" className={inputCls} value={editForm.visit_code || ""} onChange={handleEditChange} placeholder="svc_xxx" disabled={!isEditing} />
                    {isEditing && (
                      <button type="button" onClick={async () => { await handleLinkByVisitCode(); fetchSales(); setEditModal(null); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
                        <Link2 className="w-3.5 h-3.5" /> Vincular
                      </button>
                    )}
                  </div>
                </div>

                {/* GCLID */}
                <div>
                  <label className={labelCls}>GCLID <span className="text-red-500">(obrigatório para Google Ads)</span></label>
                  <input name="gclid" className={inputCls} value={editForm.gclid} onChange={handleEditChange} placeholder="GCLID" disabled={!isEditing} />
                </div>

                {/* GBRAID */}
                <div>
                  <label className={labelCls}>GBRAID</label>
                  <div className="flex gap-2">
                    <input name="gbraid" className={inputCls} value={editForm.gbraid || ""} onChange={handleEditChange} placeholder="GBRAID" disabled={!isEditing} />
                    {!!editForm.gbraid && (
                      <button type="button" onClick={() => navigator.clipboard.writeText(editForm.gbraid)} className="inline-flex items-center px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs shrink-0">Copiar</button>
                    )}
                  </div>
                </div>

                {/* WBRAID */}
                <div>
                  <label className={labelCls}>WBRAID</label>
                  <div className="flex gap-2">
                    <input name="wbraid" className={inputCls} value={editForm.wbraid || ""} onChange={handleEditChange} placeholder="WBRAID" disabled={!isEditing} />
                    {!!editForm.wbraid && (
                      <button type="button" onClick={() => navigator.clipboard.writeText(editForm.wbraid)} className="inline-flex items-center px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs shrink-0">Copiar</button>
                    )}
                  </div>
                </div>

                {/* Conversion name */}
                <div>
                  <label className={labelCls}>Nome da Conversão <span className="text-red-500">(deve ser idêntico ao criado no Google Ads)</span></label>
                  <input name="conversion_name" className={inputCls} value={editForm.conversion_name} onChange={handleEditChange} placeholder="Ex: purchase_pixel" disabled={!isEditing} />
                </div>
              </div>
            </div>

            {/* Raw webhook */}
            {editModal.raw_webhook_data && (
              <details className="mt-2 mb-4">
                <summary className="cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100">
                  Ver detalhes técnicos (JSON/Webhook)
                </summary>
                <pre className="rounded-lg p-3 mt-2 text-xs overflow-auto max-h-48 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                  {typeof editModal.raw_webhook_data === "string" ? editModal.raw_webhook_data : JSON.stringify(editModal.raw_webhook_data, null, 2)}
                </pre>
              </details>
            )}

            {/* Ações */}
            <div className="flex gap-3 justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              {!isEditing ? (
                <>
                  <button onClick={() => { if (user.plan === "free") { toast("Função exclusiva para planos pagos."); return; } setIsEditing(true); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    <EditIcon className="w-4 h-4" /> Editar
                  </button>
                  <button onClick={handleCloseEdit} className="inline-flex items-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                    Fechar
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleCloseEdit} className="inline-flex items-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                    Cancelar
                  </button>
                  <button onClick={handleSaveEdit} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    <Save className="w-4 h-4" /> Salvar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODAL CONFIRMAÇÃO DELETE ── */}
      {saleToDelete && createPortal(
        <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[1px] flex items-center justify-center">
          <div className="rounded-2xl p-6 min-w-[320px] max-w-[90vw] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 shadow-2xl">
            <h3 className="text-base font-bold mb-3">Confirmar exclusão</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
              Após <strong>deletar</strong> a venda, ela não poderá ser exibida novamente.
            </p>
            <p className="text-sm text-red-500 mb-4">Essa ação é irreversível.</p>
            <div className="mb-4">
              <label className={labelCls}>Digite <strong>deletar</strong> para confirmar:</label>
              <input className={inputCls} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="deletar" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setSaleToDelete(null); setConfirmText(""); }}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={confirmText.trim().toLowerCase() !== "deletar"}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}